import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Radio, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import EmptyState from '../../components/crm/EmptyState';
import FilterChip from '../../components/crm/FilterChip';
import { broadcastStatusMeta, audienceSummary } from '../../lib/broadcasts';
import { relativeTime, firstName, localPart } from '../../lib/communications';

const FILTERS = [['', 'All'], ['draft', 'Drafts'], ['sending', 'Sending'], ['sent', 'Sent']];

// Communications → Broadcasts: every one-email-to-many-schools send, newest
// first. "New broadcast" creates a draft and opens the editor.
export default function BroadcastsTab({ onToast }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const sp = new URLSearchParams();
      if (status) sp.set('status', status);
      const d = await api.get(`/api/admin/communications/broadcasts${sp.toString() ? `?${sp}` : ''}`);
      setRows(d?.broadcasts || []);
    } catch (err) {
      setError(err?.message || 'Failed to load broadcasts');
      setRows([]);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const d = await api.post('/api/admin/communications/broadcasts', {});
      navigate(`/admin/communications/broadcasts/${d.broadcast.id}`);
    } catch (err) {
      onToast?.({ message: 'Couldn’t start a broadcast', detail: err?.data?.error || err?.message });
      setCreating(false);
    }
  };

  return (
    <div data-testid="broadcasts-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <p className="text-[13px] text-gray-400 max-w-[60ch]">
          One email to a chosen set of schools, personalised per school and sent from a real mailbox. Every school gets its own email; replies land in Needs reply.
        </p>
        <button type="button" onClick={create} disabled={creating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-60" data-testid="new-broadcast">
          {creating ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : <Plus className="w-3 h-3" aria-hidden="true" />} New broadcast
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTERS.map(([k, label]) => <FilterChip key={k || 'all'} active={status === k} onClick={() => setStatus(k)}>{label}</FilterChip>)}
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      {rows === null ? (
        <div className="space-y-2 animate-pulse">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-white/[0.03]" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Radio} title={status ? 'Nothing here' : 'No broadcasts yet'} description={status ? 'No broadcasts with this status.' : 'Start one to email a set of schools at once — you’ll see exactly who receives what before anything sends.'} />
      ) : (
        <ul className="space-y-2 list-none m-0 p-0">
          {rows.map((b) => <BroadcastRow key={b.id} b={b} onOpen={() => navigate(`/admin/communications/broadcasts/${b.id}`)} />)}
        </ul>
      )}
    </div>
  );
}

function BroadcastRow({ b, onOpen }) {
  const meta = broadcastStatusMeta(b.status);
  const who = b.sentByName || b.createdByName;
  const when = b.finishedAt || b.startedAt || b.updatedAt || b.createdAt;
  return (
    <li>
      <button type="button" onClick={onOpen}
        className="w-full text-left p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-3"
        data-testid="broadcast-row">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[14px] text-white font-medium truncate">{b.name}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border shrink-0 ${meta.tone}`}>{meta.label}</span>
          </div>
          <div className="text-[12px] text-gray-500 mt-0.5 truncate">{audienceSummary(b.audience)}</div>
          <div className="text-[11.5px] text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
            {b.fromEmail && <span>from {localPart(b.fromEmail)}</span>}
            {who && <span>· {b.status === 'draft' ? 'started' : 'sent'} by {firstName(who)}</span>}
            {when && <span>· {relativeTime(when)}</span>}
          </div>
        </div>
        {b.status !== 'draft' && (
          <div className="text-right shrink-0 tabular-nums">
            <div className="text-[15px] text-white font-semibold">{b.sentCount}<span className="text-gray-600 text-[12px] font-normal"> / {b.recipientCount}</span></div>
            <div className="text-[11px] text-gray-500">
              sent{b.failedCount > 0 && <span className="text-red-300"> · {b.failedCount} failed</span>}{b.skippedCount > 0 && <span> · {b.skippedCount} skipped</span>}
            </div>
          </div>
        )}
      </button>
    </li>
  );
}
